import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import LoadingIndicator from '../common/LoadingIndicator';
import SyncUsers from './SyncUsers';

const ADMIN_ROLES = [
  'admin-master',
  'admin-content_manager',
  'admin-smecourse_manager',
  'admin-support_manager',
  'admin-scholarship_manager'
];

const ITEMS_PER_PAGE = 10;

function UserManagement() {
  const [allUsers, setAllUsers] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('email');
  const [sortOrder, setSortOrder] = useState('asc');
  const { user } = useAuth();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  const [userPage, setUserPage] = useState([]);
  const [pageCursors, setPageCursors] = useState([null]);
  const [lastVisibleUser, setLastVisibleUser] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);

  const loadAllUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const coll = collection(db, 'users');
      const usersSnapshot = await getDocs(coll);
      const loadedUsers = usersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setAllUsers(loadedUsers);
      setTotalUsersCount(loadedUsers.length);
    } catch (e) {
      console.error('Error loading users:', e);
      setError('Failed to load users: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUsersPage = async (page = 1, cursors = pageCursors) => {
    setLoadingPage(true);
    setError('');
    try {
      const cursor = cursors[page - 1] || null;
      let usersQuery = query(
        collection(db, 'users'),
        orderBy('email'),
        limit(ITEMS_PER_PAGE)
      );

      if (roleFilter !== 'all') {
        usersQuery = query(usersQuery, where('role', '==', roleFilter));
      }

      if (cursor) {
        usersQuery = query(usersQuery, startAfter(cursor));
      }

      const usersSnapshot = await getDocs(usersQuery);
      const loadedUsers = usersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setUserPage(loadedUsers);
      setLastVisibleUser(usersSnapshot.docs[usersSnapshot.docs.length - 1] || null);
      setHasNextPage(usersSnapshot.docs.length === ITEMS_PER_PAGE);

      if (page === cursors.length && usersSnapshot.docs.length === ITEMS_PER_PAGE) {
        setPageCursors(prev => {
          const next = [...prev];
          next[page] = usersSnapshot.docs[usersSnapshot.docs.length - 1];
          return next;
        });
      }

      setCurrentPage(page);
      setTotalUsersCount(prev => prev || loadedUsers.length);
    } catch (e) {
      console.error('Error loading user page:', e);
      setError('Failed to load users: ' + e.message);
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    const checkAndLoad = async () => {
      try {
        if (!user) {
          setError('Please log in to access this page');
          setLoading(false);
          return;
        }

        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists() || !ADMIN_ROLES.includes(userDoc.data().role)) {
          setError('Access denied. Admin privileges required.');
          setLoading(false);
          return;
        }

        if (userDoc.data().role !== 'admin-master') {
          setError('Access denied. Master admin privileges required.');
          setLoading(false);
          return;
        }

        setIsAuthorized(true);
        setCurrentPage(1);
        setPageCursors([null]);

        if (searchQuery.trim()) {
          await loadAllUsers();
        } else {
          await loadUsersPage(1, [null]);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Failed to fetch users: ' + error.message);
        setLoading(false);
      }
    };

    checkAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!isAuthorized) return;
    if (!searchQuery.trim()) {
      setCurrentPage(1);
      setPageCursors([null]);
      loadUsersPage(1, [null]);
    }
  }, [searchQuery, roleFilter, isAuthorized]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      // Validate the new role
      if (!ADMIN_ROLES.includes(newRole) && newRole !== 'user') {
        setError('Invalid role selected');
        return;
      }

      // Update both role and isAdmin fields for backward compatibility
      const isAdmin = newRole !== 'user';
      await updateDoc(doc(db, 'users', userId), {
        role: newRole,
        isAdmin: isAdmin
      });

      setAllUsers(allUsers.map(u =>
        u.id === userId
          ? { ...u, role: newRole, isAdmin: isAdmin }
          : u
      ));
    } catch (error) {
      setError('Failed to update user role');
    }
  };

  // Sorting logic
  const sortedUsers = [...allUsers].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    if (sortField === 'createdAt' || sortField === 'lastLogin') {
      aValue = aValue ? (aValue.toDate ? aValue.toDate() : new Date(aValue)) : new Date(0);
      bValue = bValue ? (bValue.toDate ? bValue.toDate() : new Date(bValue)) : new Date(0);
    } else if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const isSearchActive = searchQuery.trim().length > 0;

  const filteredUsers = sortedUsers.filter(user => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = (user.email?.toLowerCase().includes(query) ||
      user.name?.toLowerCase().includes(query));
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / ITEMS_PER_PAGE));
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const usersToDisplay = isSearchActive ? paginatedUsers : userPage;

  const handlePageChange = async (newPage) => {
    if (newPage < 1) return;

    if (searchQuery.trim()) {
      if (newPage <= totalPages) {
        setCurrentPage(newPage);
      }
      return;
    }

    if (newPage === currentPage) return;
    if (newPage > currentPage && !hasNextPage) return;

    await loadUsersPage(newPage);
  };

  // CSV Export function
  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Role', 'Signup Date', 'Last Login'];
    const rows = usersToDisplay.map(user => [
      user.name || 'N/A',
      user.email || '',
      user.role || (user.isAdmin ? 'ADMIN' : 'User'),
      user.createdAt
        ? (user.createdAt.toDate ? user.createdAt.toDate().toISOString() : new Date(user.createdAt).toISOString())
        : '',
      user.lastLogin
        ? (user.lastLogin.toDate ? user.lastLogin.toDate().toISOString() : new Date(user.lastLogin).toISOString())
        : ''
    ]);
    let csvContent = headers.join(',') + '\n' + rows.map(r => r.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'users_export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <LoadingIndicator />;
  if (error) return <div className="text-red-600 p-4">{error}</div>;

  return (
    <>
      <SyncUsers />

      <section className="bg-white p-4 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold flex items-center gap-3">
            User Management
            <span className="text-sm font-normal text-gray-600 bg-gray-100 px-2 py-1 rounded">
              Total: {totalUsersCount}
            </span>
          </h2>
          <button
            onClick={exportToCSV}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Export CSV
          </button>
        </div>

        <div className="flex gap-4 mb-4">
          <input
            type="text"
            placeholder="Search by name or email"
            value={searchQuery}
            onChange={(e) => {
              const value = e.target.value;
              setSearchQuery(value);
              if (value.trim() && allUsers.length === 0) {
                loadAllUsers();
              }
            }}
            className="flex-1 p-2 border rounded"
          />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="p-2 border rounded"
          >
            <option value="all">All Roles</option>
            <option value="user">User</option>
            {ADMIN_ROLES.map(role => (
              <option key={role} value={role}>
                {role.replace('admin-', '').replace('_', ' ').toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none"
                  onClick={() => {
                    if (sortField === 'createdAt') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('createdAt');
                      setSortOrder('desc');
                    }
                  }}
                >
                  Signup Date
                  {sortField === 'createdAt' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none"
                  onClick={() => {
                    if (sortField === 'lastLogin') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('lastLogin');
                      setSortOrder('desc');
                    }
                  }}
                >
                  Last Login
                  {sortField === 'lastLogin' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {usersToDisplay.map(user => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{user.name || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{user.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {user.role ? (
                      user.role.startsWith('admin-')
                        ? user.role.replace('admin-', '').replace('_', ' ').toUpperCase()
                        : user.role
                    ) : (user.isAdmin ? 'ADMIN' : 'User')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {user.createdAt
                      ? (user.createdAt.toDate ? user.createdAt.toDate().toLocaleString() : new Date(user.createdAt).toLocaleString())
                      : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {user.lastLogin
                      ? (user.lastLogin.toDate ? user.lastLogin.toDate().toLocaleString() : new Date(user.lastLogin).toLocaleString())
                      : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <select
                      value={user.role || (user.isAdmin ? 'admin-master' : 'user')}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      className="p-1 border rounded"
                    >
                      <option value="user">User</option>
                      {ADMIN_ROLES.map(role => (
                        <option key={role} value={role}>
                          {role.replace('admin-', '').replace('_', ' ').toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">
              Showing {usersToDisplay.length > 0 ? ((currentPage - 1) * ITEMS_PER_PAGE) + 1 : 0} to{' '}
              {((currentPage - 1) * ITEMS_PER_PAGE) + usersToDisplay.length} {' '}
              {isSearchActive ? `of ${totalUsersCount} total users` : 'on this page'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded ${currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-[rgb(130,88,18)] text-white hover:bg-[rgb(110,68,0)]'
                }`}
            >
              Previous
            </button>

            {/* Jump to page input */}
            <div className="flex items-center space-x-1">
              <span className="text-sm text-gray-700">Jump to</span>
              <input
                type="number"
                min="1"
                max={isSearchActive ? totalPages : currentPage + (hasNextPage ? 1 : 0)}
                value={currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value, 10);
                  if (page >= 1) {
                    handlePageChange(page);
                  }
                }}
                className="w-16 px-2 py-1 border rounded text-center"
              />
              <span className="text-sm text-gray-700">of {isSearchActive ? totalPages : currentPage + (hasNextPage ? 1 : 0)}</span>
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!hasNextPage && !isSearchActive}
              className={`px-3 py-1 rounded ${currentPage === totalPages
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-[rgb(130,88,18)] text-white hover:bg-[rgb(110,68,0)]'
                }`}
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

export default UserManagement;